import { ReactNode } from 'react';
import { Package, FileText, Truck, ClipboardList, MapPin, Box, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Issue #28: Standardized empty state handling across all tables

export type EmptyStateVariant =
  | 'inventory'
  | 'work-orders'
  | 'picking'
  | 'shipping'
  | 'receiving'
  | 'locations'
  | 'serials'
  | 'search'
  | 'generic';

interface EmptyStateProps {
  /** The type of content that is empty - determines icon and default message */
  variant?: EmptyStateVariant;
  /** Custom title - overrides default */
  title?: string;
  /** Custom description - overrides default */
  description?: string;
  /** Show search-related messaging (e.g., "No results matching your criteria") */
  isFiltered?: boolean;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Optional secondary action */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Custom icon - overrides variant icon */
  icon?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const VARIANT_CONFIG: Record<EmptyStateVariant, {
  icon: ReactNode;
  title: string;
  description: string;
  filteredTitle: string;
  filteredDescription: string;
}> = {
  inventory: {
    icon: <Package className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No inventory items',
    description: 'Get started by adding inventory to your warehouse.',
    filteredTitle: 'No inventory items found',
    filteredDescription: 'No items match your current filters. Try adjusting your search criteria.',
  },
  'work-orders': {
    icon: <ClipboardList className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No work orders',
    description: 'Create your first work order to get started.',
    filteredTitle: 'No work orders found',
    filteredDescription: 'No work orders match your current filters. Try adjusting your search criteria.',
  },
  picking: {
    icon: <Box className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No picking lists',
    description: 'Picking lists will appear here when work orders are ready for picking.',
    filteredTitle: 'No picking lists found',
    filteredDescription: 'No picking lists match your current filters. Try adjusting your search criteria.',
  },
  shipping: {
    icon: <Truck className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No packing slips',
    description: 'Packing slips will appear here when items are ready for shipping.',
    filteredTitle: 'No packing slips found',
    filteredDescription: 'No packing slips match your current filters. Try adjusting your search criteria.',
  },
  receiving: {
    icon: <FileText className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No receiving orders',
    description: 'Create a receiving order to start receiving inventory.',
    filteredTitle: 'No receiving orders found',
    filteredDescription: 'No receiving orders match your current filters. Try adjusting your search criteria.',
  },
  locations: {
    icon: <MapPin className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No locations',
    description: 'Set up zones and locations to organize your warehouse.',
    filteredTitle: 'No locations found',
    filteredDescription: 'No locations match your current filters. Try adjusting your search criteria.',
  },
  serials: {
    icon: <Package className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No serialized items',
    description: 'Serialized items will appear here when they are received or created.',
    filteredTitle: 'No serialized items found',
    filteredDescription: 'No serialized items match your current filters. Try adjusting your search criteria.',
  },
  search: {
    icon: <Search className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No results',
    description: 'Try adjusting your search or filter criteria.',
    filteredTitle: 'No results found',
    filteredDescription: 'No items match your search. Try different keywords or clear filters.',
  },
  generic: {
    icon: <Package className="h-12 w-12 text-muted-foreground/50" />,
    title: 'No items',
    description: 'There are no items to display.',
    filteredTitle: 'No items found',
    filteredDescription: 'No items match your current filters. Try adjusting your search criteria.',
  },
};

export function EmptyState({
  variant = 'generic',
  title,
  description,
  isFiltered = false,
  action,
  secondaryAction,
  icon,
  className,
}: EmptyStateProps) {
  const config = VARIANT_CONFIG[variant];

  const displayTitle = title ?? (isFiltered ? config.filteredTitle : config.title);
  const displayDescription = description ?? (isFiltered ? config.filteredDescription : config.description);
  const displayIcon = icon ?? config.icon;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
      role="status"
      aria-label={displayTitle}
    >
      <div className="mb-4">
        {displayIcon}
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">
        {displayTitle}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        {displayDescription}
      </p>
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {secondaryAction && (
            <Button
              variant="outline"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
          {action && (
            <Button onClick={action.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Convenience exports for common empty states
export function InventoryEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="inventory" {...props} />;
}

export function WorkOrdersEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="work-orders" {...props} />;
}

export function PickingEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="picking" {...props} />;
}

export function ShippingEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="shipping" {...props} />;
}

export function ReceivingEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="receiving" {...props} />;
}

export function LocationsEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="locations" {...props} />;
}

export function SerialsEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="serials" {...props} />;
}

export function SearchEmptyState(props: Omit<EmptyStateProps, 'variant'>) {
  return <EmptyState variant="search" {...props} />;
}
